
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import Layout from '@/components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  Play, 
  Clock, 
  ChefHat, 
  Calendar, 
  ShoppingCart,
  Download,
  Rocket,
  Loader2,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import { Tutorial, TutorialCategory } from '@/types/tutorials';

const iconMap = {
  'rocket': Rocket,
  'chef-hat': ChefHat,
  'calendar': Calendar,
  'shopping-cart': ShoppingCart,
  'book-open': BookOpen,
  'download': Download,
};

const Tutorials = () => {
  const [categories, setCategories] = useState<TutorialCategory[]>([]);
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);

  useEffect(() => {
    fetchCategoriesAndTutorials();
  }, []);

  const fetchCategoriesAndTutorials = async () => {
    try {
      setLoading(true);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('tutorial_categories')
        .select('*')
        .order('order_index', { ascending: true });

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
        toast.error('Error cargando categorías');
        return;
      }

      // Fetch tutorials with categories
      const { data: tutorialsData, error: tutorialsError } = await supabase
        .from('tutorials')
        .select(`
          *,
          category:tutorial_categories(*)
        `)
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (tutorialsError) {
        console.error('Error fetching tutorials:', tutorialsError);
        toast.error('Error cargando tutoriales');
        return;
      }

      setCategories(categoriesData || []);
      setTutorials(tutorialsData || []);
    } catch (error) {
      console.error('Error in fetchCategoriesAndTutorials:', error);
      toast.error('Error cargando tutoriales');
    } finally {
      setLoading(false);
    }
  };

  const filteredTutorials = selectedCategory === 'all' 
    ? tutorials 
    : tutorials.filter(tutorial => tutorial.category_id === selectedCategory);

  const renderVideoEmbed = (tutorial: Tutorial) => {
    if (!tutorial.video_url || tutorial.video_type === 'none') return null;

    if (tutorial.video_type === 'youtube') {
      const videoId = tutorial.video_url.includes('youtu.be') 
        ? tutorial.video_url.split('/').pop()?.split('?')[0]
        : tutorial.video_url.split('v=')[1]?.split('&')[0];
      
      if (videoId) {
        return (
          <div className="relative w-full h-0 pb-[56.25%] mb-6 md:mb-8 rounded-lg md:rounded-xl overflow-hidden shadow-lg">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}`}
              title={tutorial.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    }

    if (tutorial.video_type === 'vimeo') {
      const videoId = tutorial.video_url.split('/').pop();
      if (videoId) {
        return (
          <div className="relative w-full h-0 pb-[56.25%] mb-6 md:mb-8 rounded-lg md:rounded-xl overflow-hidden shadow-lg">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://player.vimeo.com/video/${videoId}`}
              title={tutorial.title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    }

    return null;
  };

  const formatTutorialContent = (content: string) => {
    // Check if content contains HTML tags (likely uploaded from admin dashboard)
    if (content.includes('<div') || content.includes('<h2') || content.includes('<p>') || content.includes('<ol>') || content.includes('<ul>')) {
      return (
        <div className="tutorial-html-content bg-white rounded-lg md:rounded-2xl p-4 md:p-8 shadow-sm border border-gray-100">
          <div 
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      );
    }

    // Original formatting logic for plain text content
    const paragraphs = content.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((paragraph, index) => {
      const trimmedParagraph = paragraph.trim();
      
      // Check if it's a title (starts with emoji and has specific patterns)
      if (trimmedParagraph.match(/^[🍌🔍💚🛒⭐🎯📱✨🌱💪📊🎉]/)) {
        return (
          <div key={index} className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-vegana-primary mb-4 md:mb-6 flex items-start md:items-center gap-2 md:gap-3 pb-2 md:pb-3 border-b-2 border-vegana-primary/20 leading-tight">
              {trimmedParagraph}
            </h2>
          </div>
        );
      }
      
      // Check if it's a list (contains numbered items or bullet points)
      if (trimmedParagraph.includes('1.') || trimmedParagraph.includes('2.') || trimmedParagraph.includes('•')) {
        const items = trimmedParagraph.split(/(?=\d+\.|•)/).filter(item => item.trim());
        return (
          <div key={index} className="mb-6 md:mb-8">
            <ol className="space-y-3 md:space-y-4 pl-3 md:pl-6">
              {items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start gap-3 md:gap-4 relative">
                  <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-vegana-primary text-white rounded-full text-xs md:text-sm font-bold flex-shrink-0 mt-1">
                    {itemIndex + 1}
                  </span>
                  <div className="text-gray-700 leading-relaxed text-sm md:text-lg flex-1">
                    {item.replace(/^\d+\.|^•/, '').trim()}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        );
      }
      
      // Regular paragraph
      return (
        <div key={index} className="mb-6 md:mb-8 p-4 md:p-6 bg-gray-50 rounded-lg border-l-4 border-vegana-primary">
          <p className="text-gray-700 leading-relaxed text-sm md:text-lg m-0">
            {trimmedParagraph}
          </p>
        </div>
      );
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-vegana-primary" />
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  // Show selected tutorial detail view
  if (selectedTutorial) {
    const IconComponent = selectedTutorial.category?.icon 
      ? iconMap[selectedTutorial.category.icon as keyof typeof iconMap] || BookOpen
      : BookOpen;

    return (
      <ProtectedRoute>
        <Layout>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto px-3 md:px-4 py-4 md:py-8"
          >
            <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
              <Button 
                variant="outline" 
                onClick={() => setSelectedTutorial(null)}
                className="flex items-center gap-2 hover:bg-vegana-primary hover:text-white transition-colors text-sm md:text-base"
                size="sm"
              >
                <ArrowLeft className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Volver a Tutoriales</span>
                <span className="sm:hidden">Volver</span>
              </Button>
            </div>

            <Card className="border-0 shadow-lg md:shadow-xl bg-gradient-to-br from-white to-gray-50 overflow-hidden">
              <div className="h-2 md:h-3 bg-gradient-to-r from-vegana-primary to-vegana-secondary"></div>
              
              <CardHeader className="bg-white pb-4 md:pb-8 p-4 md:p-6">
                <div className="flex items-start gap-3 md:gap-6">
                  <div className="p-2 md:p-4 bg-vegana-primary/10 rounded-xl md:rounded-2xl flex-shrink-0">
                    <IconComponent className="h-6 w-6 md:h-10 md:w-10 text-vegana-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4 leading-tight break-words">
                      {selectedTutorial.title}
                    </CardTitle>
                    {selectedTutorial.description && (
                      <CardDescription className="text-sm md:text-xl text-gray-600 leading-relaxed mb-4 md:mb-6">
                        {selectedTutorial.description}
                      </CardDescription>
                    )}
                    <div className="flex flex-wrap items-center gap-2 md:gap-4">
                      {selectedTutorial.video_url && selectedTutorial.video_type !== 'none' && (
                        <Badge variant="secondary" className="flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1 md:py-2 bg-blue-100 text-blue-700 border-0 text-xs md:text-sm">
                          <Play className="h-3 w-3 md:h-4 md:w-4" />
                          Video Tutorial
                        </Badge>
                      )}
                      {selectedTutorial.category && (
                        <Badge variant="outline" className="px-2 md:px-4 py-1 md:py-2 border-vegana-primary text-vegana-primary text-xs md:text-sm">
                          {selectedTutorial.category.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-4 md:p-8">
                {renderVideoEmbed(selectedTutorial)}
                
                <div className="prose prose-sm md:prose-xl max-w-none">
                  <div className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-10 shadow-sm border border-gray-100">
                    <div className="tutorial-content max-w-4xl">
                      {formatTutorialContent(selectedTutorial.content)}
                    </div>
                  </div>
                </div>

                <div className="mt-6 md:mt-10 p-4 md:p-8 bg-gradient-to-r from-vegana-primary/5 to-vegana-secondary/5 rounded-2xl md:rounded-3xl border border-vegana-primary/10">
                  <div className="flex items-start md:items-center gap-3 md:gap-4">
                    <div className="p-2 md:p-3 bg-vegana-primary/10 rounded-lg md:rounded-xl flex-shrink-0">
                      <BookOpen className="h-4 w-4 md:h-6 md:w-6 text-vegana-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm md:text-lg mb-1">¿Te ha sido útil este tutorial?</h4>
                      <p className="text-gray-600 text-xs md:text-base">Si tienes dudas, puedes contactar con nuestro soporte.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </Layout>
      </ProtectedRoute>
    );
  }

  // Show tutorials grid view
  return (
    <ProtectedRoute>
      <Layout>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-6 md:space-y-8 px-3 md:px-0"
        >
          <motion.div variants={itemVariants} className="text-center max-w-3xl mx-auto px-2">
            <h1 className="text-2xl md:text-4xl font-bold text-vegana-primary mb-3 md:mb-4">Tutoriales</h1>
            <p className="text-sm md:text-lg text-gray-600">
              Aprende a usar todas las funciones de Guía Vegana App
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="px-1 md:px-0">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 w-full mb-6 md:mb-8 h-auto p-1">
                <TabsTrigger value="all" className="text-xs md:text-sm py-2 md:py-2.5">Todos</TabsTrigger>
                {categories.map((category) => {
                  const IconComponent = iconMap[category.icon as keyof typeof iconMap] || BookOpen;
                  return (
                    <TabsTrigger key={category.id} value={category.id} className="text-xs md:text-sm flex items-center gap-1 py-2 md:py-2.5">
                      <IconComponent className="h-3 w-3 md:h-4 md:w-4" />
                      <span className="hidden sm:inline">{category.name}</span>
                      <span className="sm:hidden text-xs">{category.name.slice(0, 8)}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredTutorials.length === 0 ? (
                  <div className="col-span-full">
                    <Card>
                      <CardContent className="flex items-center justify-center py-8 md:py-12">
                        <div className="text-center">
                          <BookOpen className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                          <h3 className="text-base md:text-lg font-medium text-gray-600 mb-2">
                            No hay tutoriales disponibles
                          </h3>
                          <p className="text-sm md:text-base text-gray-500">
                            {selectedCategory === 'all' 
                              ? 'Pronto añadiremos tutoriales para ayudarte.' 
                              : 'No hay tutoriales en esta categoría todavía.'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  filteredTutorials.map((tutorial, index) => {
                    const IconComponent = tutorial.category?.icon 
                      ? iconMap[tutorial.category.icon as keyof typeof iconMap] || BookOpen
                      : BookOpen;
                    
                    return (
                      <motion.div
                        key={tutorial.id}
                        variants={itemVariants}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card 
                          className="border-t-4 border-vegana-primary hover:shadow-lg transition-shadow cursor-pointer h-full"
                          onClick={() => setSelectedTutorial(tutorial)}
                        >
                          <CardHeader className="pb-2 md:pb-3 p-4 md:p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <CardTitle className="text-sm md:text-lg text-vegana-primary flex items-center gap-2 mb-2 break-words">
                                  <IconComponent className="h-4 w-4 md:h-5 md:w-5 text-vegana-secondary flex-shrink-0" />
                                  <span className="leading-tight">{tutorial.title}</span>
                                </CardTitle>
                                {tutorial.description && (
                                  <CardDescription className="text-xs md:text-sm line-clamp-2 leading-relaxed">
                                    {tutorial.description}
                                  </CardDescription>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0 p-4 md:p-6 md:pt-0">
                            <div className="flex items-center justify-between">
                              <div className="flex flex-wrap items-center gap-1 md:gap-2">
                                {tutorial.video_url && tutorial.video_type !== 'none' && (
                                  <Badge variant="secondary" className="flex items-center gap-1 text-xs px-2 py-1">
                                    <Play className="h-2 w-2 md:h-3 md:w-3" />
                                    Video
                                  </Badge>
                                )}
                                {tutorial.category && (
                                  <Badge variant="outline" className="text-xs px-2 py-1">
                                    {tutorial.category.name}
                                  </Badge>
                                )}
                              </div>
                              <ArrowRight className="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </Tabs>
          </motion.div>
        </motion.div>
      </Layout>
    </ProtectedRoute>
  );
};

export default Tutorials;
